import { useDeployedBuildReload } from '@/hooks/useDeployedBuildReload';
import { Suspense, useEffect } from 'react';
import { createBrowserRouter, Navigate, Outlet, ScrollRestoration, useLocation, useParams } from 'react-router-dom';
import { lazyNamed } from './lazyNamed';
import { Box, Spinner } from '@/components/primitives';
import { ProtectedRoute } from './ProtectedRoute';
import { RequireContext } from './RequireContext';
import { RouteErrorBoundary } from '@/components/errors';
import { AppShell } from '@/components/layout';
import { ClickBusyCursor } from '@/components/layout/ClickBusyCursor';
import { AccountMergeHost } from '@/components/dialogs/AccountMergeDialog';
import { PasswordPromptHost } from '@/features/accounts/components/PasswordPromptHost';
import { AreaProvider } from '@/contexts';
import { SPINNER_SIZE } from '@/components/ui/LoadingZone';
import { vars } from '@/theme';
import { paths } from '@/constants';
import { addBreadcrumb } from '@/lib/breadcrumbs';
import { CtxParamGate } from '@/features/context/components/CtxParamGate';

// Lazy load feature modules for code splitting. lazyNamed (not bare lazy):
// it holds the Suspense fallback when a stale-deploy recovery reload is in
// flight instead of crashing the route boundary.
const SignIn = lazyNamed(() => import('@/features/accounts/components/SignIn/SignIn'), (m) => m.SignIn);
const SignUp = lazyNamed(() => import('@/features/accounts/components/SignUp/SignUp'), (m) => m.SignUp);
const VerifyCode = lazyNamed(() => import('@/features/accounts/components/VerifyCode/VerifyCode'), (m) => m.VerifyCode);
const ForgotPassword = lazyNamed(() => import('@/features/accounts/components/ForgotPassword/ForgotPassword'), (m) => m.ForgotPassword);
const Sso = lazyNamed(() => import('@/features/accounts/components/Sso/Sso'), (m) => m.Sso);
const Logout = lazyNamed(() => import('@/features/accounts/components/Logout/Logout'), (m) => m.Logout);

const AnalyticsDashboard = lazyNamed(() => import('@/features/dashboard/components/AnalyticsDashboard/AnalyticsDashboard'), (m) => m.AnalyticsDashboard);
const ContactsList = lazyNamed(() => import('@/features/contacts/components/ContactsList/ContactsList'), (m) => m.ContactsList);
const ContactDetail = lazyNamed(() => import('@/features/contacts/components/ContactDetail/ContactDetail'), (m) => m.ContactDetail);
const PacketsList = lazyNamed(() => import('@/features/packets/components/PacketsList/PacketsList'), (m) => m.PacketsList);
const PacketEdit = lazyNamed(() => import('@/features/packets/components/PacketEdit/PacketEdit'), (m) => m.PacketEdit);
const PacketDetailPage = lazyNamed(() => import('@/features/packets/components/PacketDetailPage/PacketDetailPage'), (m) => m.PacketDetailPage);
const ChecklistsList = lazyNamed(() => import('@/features/checklists/components/ChecklistsList/ChecklistsList'), (m) => m.ChecklistsList);
const ChecklistEdit = lazyNamed(() => import('@/features/checklists/components/ChecklistEdit/ChecklistEdit'), (m) => m.ChecklistEdit);
const ChecklistDetailPage = lazyNamed(() => import('@/features/checklists/components/ChecklistDetailPage/ChecklistDetailPage'), (m) => m.ChecklistDetailPage);
const ViewContainer = lazyNamed(() => import('@/features/consume/components/ViewContainer/ViewContainer'), (m) => m.ViewContainer);
const ViewerDashboard = lazyNamed(() => import('@/features/home/components/ViewerDashboard/ViewerDashboard'), (m) => m.ViewerDashboard);
const CertificatesView = lazyNamed(() => import('@/features/home/components/CertificatesView/CertificatesView'), (m) => m.CertificatesView);
const LibraryView = lazyNamed(() => import('@/features/home/components/LibraryView/LibraryView'), (m) => m.LibraryView);
const AdminArea = lazyNamed(() => import('@/features/admin'), (m) => m.AdminArea);
const AnalyticsPage = lazyNamed(() => import('@/features/analytics/components/AnalyticsPage/AnalyticsPage'), (m) => m.AnalyticsPage);
const StaffManagementPage = lazyNamed(() => import('@/features/staff'), (m) => m.StaffManagementPage);
const ReportLatestPage = lazyNamed(() => import('@/features/staff'), (m) => m.ReportLatestPage);
const NotFound = lazyNamed(() => import('@/components/pages/NotFound'), (m) => m.NotFound);

// Loading fallback component
const PageLoader = () => (
  <Box display="flex" justifyContent="center" alignItems="center" style={{ minHeight: '50vh' }}>
    <Spinner size={SPINNER_SIZE} color={vars.color.success.base} />
  </Box>
);

/**
 * Integrations moved from a standalone /manage/integrations page to a tab on
 * the /manage page. Old links (incl. deep-links to a source's mappings) forward
 * to the tab, preserving the selected integration via `?source=`.
 */
const ManageIntegrationsRedirect = () => {
  const { sourceType } = useParams<{ sourceType?: string }>();
  const to = sourceType
    ? `${paths.manage}?tab=integrations&source=${encodeURIComponent(sourceType)}`
    : `${paths.manage}?tab=integrations`;
  // keep-search-exempt: manage-integrations-redirect
  return <Navigate to={to} replace />;
};

/**
 * vingreact#106: `/login` forwards to the sign-in form, and the query string
 * has to survive the hop. vingapi redirects a failed magic-link redemption to
 * `/login?error=<message>`; a bare `<Navigate to="signIn">` dropped the search
 * entirely, so the guidance never reached SignIn. Forward the whole search —
 * not a per-param allowlist — so `reset=1`, `redirect=`, `auth_as=` and
 * anything added later ride along for free.
 */
const LoginIndexRedirect = () => {
  const { search } = useLocation();
  return <Navigate to={{ pathname: paths.loginSignIn, search }} replace />;
};

/**
 * #1111: the legacy `/login/forgotPassword` address forwards to
 * `/reset-password` with its query string, so a learner who reached it from a
 * course (`?redirect=/view/…`, `auth_as=`) keeps the way back. A bare
 * `<Navigate to={paths.resetPassword}>` dropped the search.
 */
const ForgotPasswordAliasRedirect = () => {
  const { search } = useLocation();
  return <Navigate to={{ pathname: paths.resetPassword, search }} replace />;
};

/**
 * vingreact#224: `/admin` → `/admin/alerts` must carry the query string, same
 * pattern as LoginIndexRedirect. See redirects.keepSearch.guard.test.ts.
 */
const AdminIndexRedirect = () => {
  const { search } = useLocation();
  return <Navigate to={{ pathname: paths.adminTab('alerts'), search }} replace />;
};

const LoginLayout = () => (
  <Suspense fallback={<PageLoader />}>
    <Outlet />
  </Suspense>
);

const PublicLayout = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<PageLoader />}>{children}</Suspense>
);

/**
 * Root layout: owns `<ScrollRestoration />` so back/forward + refresh restore
 * scroll position on every route. ScrollRestoration must live inside a data
 * router (`createBrowserRouter`); mounting it here covers every child route in
 * one place. Tests that wrap components in `BrowserRouter` skip this layer
 * entirely, which is why test setups don't hit the "useMatches must be used
 * within a data router" check.
 */
/** Records each route change as a breadcrumb for crash-report context. */
const RouteBreadcrumbs = () => {
  const location = useLocation();
  useEffect(() => {
    addBreadcrumb('route', `${location.pathname}${location.search}`);
  }, [location.pathname, location.search]);
  return null;
};

/** Stale build ⇒ the next page change is a full page load (Q-506 card 1573). */
const DeployedBuildReload = () => {
  useDeployedBuildReload();
  return null;
};

const RootLayout = () => (
  <>
    <ClickBusyCursor />
    <DeployedBuildReload />
    <ScrollRestoration />
    <RouteBreadcrumbs />
    {/* App-wide account-merge dialog: proactive suggestions from any
        auth/me refresh (incl. login flows) and 403 denial-time delivery.
        Mounted here so it has router context, covers public + protected
        trees, and survives navigation. Suppresses itself under /view. */}
    <AccountMergeHost />
    {/* Set-a-password prompt (Q-506 card 1562): opens over the page the person landed on. */}
    <PasswordPromptHost />
    <Outlet />
  </>
);

export const router = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
  // Public routes - Login
  {
    path: '/login',
    element: <LoginLayout />,
    errorElement: <RouteErrorBoundary />,
    children: [
      { index: true, element: <LoginIndexRedirect /> },
      { path: 'signIn', element: <SignIn /> },
      { path: 'signUp', element: <SignUp /> },
      { path: 'verify', element: <VerifyCode /> },
      { path: 'forgotPassword', element: <ForgotPasswordAliasRedirect /> },
    ],
  },
  { path: '/sso', element: <PublicLayout><Sso /></PublicLayout>, errorElement: <RouteErrorBoundary /> },
  { path: '/reset-password', element: <PublicLayout><ForgotPassword /></PublicLayout>, errorElement: <RouteErrorBoundary /> },
  { path: '/logout', element: <PublicLayout><Logout /></PublicLayout>, errorElement: <RouteErrorBoundary /> },

  // Public view routes (no auth required) - uses AppShell for consistent header
  {
    // `:link?` is the recipient-link segment the server serves once
    // `shorturl.link_in_path` is on (vingapi#1123). Accepted, not yet used.
    path: '/view/:stub/:link?',
    // The learner surface: no context-scoped chrome (notifications bell, forced
    // context chooser). A share-link visitor has no org/network context and can
    // never acquire one here — vingreact#64.
    element: <AppShell surface="consume" />,
    errorElement: <RouteErrorBoundary />,
    children: [
      { index: true, element: <ViewContainer /> },
    ],
  },

  // Protected routes
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <AppShell />
      </ProtectedRoute>
    ),
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        // ctx-param gate (#404): switches context before any child renders
        // when the URL carries ?ctx=org:<id> or ?ctx=network:<id>. Pathless
        // layout route above every per-route ProtectedRoute so no level check
        // runs against the old context.
        element: <CtxParamGate />,
        children: [
      // Admin/Manager analytics dashboard at root. Level-gated: viewers who
      // arrive at '/' directly (bookmark, typed URL, stale link) are bounced
      // to their home (/dashboard) via getDefaultRoute — the outer '/'
      // ProtectedRoute only checks authentication.
      {
        index: true,
        element: (
          <ProtectedRoute requiredLevel={['admin', 'manager']}>
            {/* Every dashboard query is org/network scoped: with no context
                selected they all answer 400 and the page renders blank
                (vingreact#64). Show the context chooser instead. */}
            <RequireContext>
              <AnalyticsDashboard />
            </RequireContext>
          </ProtectedRoute>
        ),
      },

      // Viewer home
      {
        path: 'dashboard',
        children: [
          { index: true, element: <ViewerDashboard /> },
          { path: 'certificates', element: <CertificatesView /> },
          { path: 'library', element: <LibraryView /> },
        ],
      },

      // Packets
      {
        path: 'packets',
        element: (
          <ProtectedRoute requiredLevel={['admin', 'manager']}>
            <AreaProvider area="packets">
              <Outlet />
            </AreaProvider>
          </ProtectedRoute>
        ),
        children: [
          { index: true, element: <PacketsList /> },
          { path: ':id', element: <PacketDetailPage /> },
          { path: ':id/edit', element: <PacketEdit /> },
        ],
      },

      // Checklists
      {
        path: 'checklists',
        element: (
          <ProtectedRoute requiredLevel={['admin', 'manager']}>
            <AreaProvider area="checklists">
              <Outlet />
            </AreaProvider>
          </ProtectedRoute>
        ),
        children: [
          { index: true, element: <ChecklistsList /> },
          { path: ':id', element: <ChecklistDetailPage /> },
          { path: ':id/edit', element: <ChecklistEdit /> },
        ],
      },

      // Contacts (admin/manager only)
      {
        path: 'contacts',
        element: (
          <ProtectedRoute requiredLevel={['admin', 'manager']}>
            <AreaProvider area="contacts">
              <Outlet />
            </AreaProvider>
          </ProtectedRoute>
        ),
        children: [
          { index: true, element: <ContactsList /> },
          { path: ':contactId', element: <ContactDetail /> },
          { path: ':contactId/:tab', element: <ContactDetail /> },
        ],
      },


      // Analytics (admin/manager with packet or checklist access)
      {
        path: 'analytics',
        element: (
          <ProtectedRoute requiredLevel={['admin', 'manager']}>
            <AreaProvider area="analytics">
              <Outlet />
            </AreaProvider>
          </ProtectedRoute>
        ),
        children: [
          { index: true, element: <AnalyticsPage /> },
          { path: ':tab', element: <AnalyticsPage /> },
        ],
      },

      // Admin area (superuser only)
      {
        path: 'admin',
        element: (
          <ProtectedRoute requireSuperuser>
            <AdminArea />
          </ProtectedRoute>
        ),
        children: [
          { index: true, element: <AdminIndexRedirect /> },
          { path: ':tool', element: null },
        ],
      },

      // Staff report download (staff only). JSON endpoint → signed URL download.
      {
        path: 'reports/:key/latest',
        element: (
          <ProtectedRoute requireStaff>
            <ReportLatestPage />
          </ProtectedRoute>
        ),
      },

      // Staff management (staff only). Integrations is now a tab on this page
      // (staff-only); the old /manage/integrations routes redirect into it.
      {
        path: 'manage',
        children: [
          {
            index: true,
            element: (
              <ProtectedRoute requireStaff>
                <StaffManagementPage />
              </ProtectedRoute>
            ),
          },
          { path: 'integrations', element: <ManageIntegrationsRedirect /> },
          { path: 'integrations/:sourceType', element: <ManageIntegrationsRedirect /> },
          { path: 'integrations/:sourceType/mappings', element: <ManageIntegrationsRedirect /> },
        ],
      },
        ], // end CtxParamGate children
      },
    ],
  },

  // 404 catch-all
  { path: '*', element: <PublicLayout><NotFound /></PublicLayout>, errorElement: <RouteErrorBoundary /> },
    ],
  },
]);

export { ProtectedRoute } from './ProtectedRoute';
